// Module Gestion des Logements - Interface Admin
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiHome, FiPlus, FiEdit, FiTrash2, FiEye, FiMapPin,
  FiDollarSign, FiSquare, FiCalendar, FiCheck, FiX
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { HousingFormModal } from '@/components/admin/HousingFormModal';
import { Housing } from '@/models';
import { HousingsServices } from '@/services/HousingServices';

// Types pour l'affichage (simplifié depuis le modèle complet)
interface HousingDisplay extends Housing {
  photos_count?: number;
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: 0;
`;

const AddButton = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary}DD;
    transform: translateY(-2px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
`;

const StatCard = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
`;

const FiltersBar = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  display: flex;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  background: white;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 250px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primary}20;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${theme.colors.surface};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.surface};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface}50;
  }
`;

const TableHeader = styled.th`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  text-align: left;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
`;

const TableCell = styled.td`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
`;

const HousingReference = styled.div`
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.xs};
`;

const HousingType = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const AddressInfo = styled.div`
  display: flex;
  align-items: start;
  gap: ${theme.spacing.sm};

  svg {
    color: ${theme.colors.text}60;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const AddressText = styled.div`
  font-size: ${theme.typography.sizes.sm};
  line-height: 1.4;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.sizes.sm};
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${theme.colors.text}80;

  svg {
    color: ${theme.colors.primary};
    font-size: 14px;
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};

  ${props => {
    switch (props.status) {
      case 'available':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'attributed':
        return `
          background: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'maintenance':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: ${theme.spacing.sm};
  background: transparent;
  border: 1px solid ${props => {
    if (props.variant === 'danger') return theme.colors.error;
    if (props.variant === 'primary') return theme.colors.primary;
    return theme.colors.text + '40';
  }};
  color: ${props => {
    if (props.variant === 'danger') return theme.colors.error;
    if (props.variant === 'primary') return theme.colors.primary;
    return theme.colors.text;
  }};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${props => {
      if (props.variant === 'danger') return theme.colors.error + '10';
      if (props.variant === 'primary') return theme.colors.primary + '10';
      return theme.colors.surface;
    }};
  }

  svg {
    font-size: 16px;
  }
`;

export const HousingManagement: React.FC = () => {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editingHousing, setEditingHousing] = useState<Housing | undefined>(undefined);
  const [housings, setHousings] = useState<Housing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch housings from API
  const fetchHousings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await HousingsServices.list() as any;
      setHousings(response.data || []);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des logements');
      console.error('Error fetching housings:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load housings on component mount
  useEffect(() => {
    fetchHousings();
  }, []);

  const stats = {
    total: housings.length,
    available: housings.filter(h => h.status === 'available').length,
    attributed: housings.filter(h => h.status === 'attributed').length,
    maintenance: housings.filter(h => h.status === 'maintenance').length
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Disponible';
      case 'attributed': return 'Attribué';
      case 'maintenance': return 'Maintenance';
      default: return status;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'available': return <FiCheck />;
      case 'attributed': return <FiHome />;
      case 'maintenance': return <FiX />;
      default: return null;
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce logement ?')) {
      try {
        await HousingsServices.delete(id);
        alert('Logement supprimé avec succès');
        fetchHousings(); // Refresh the list
      } catch (err: any) {
        alert(`Erreur lors de la suppression: ${err.message}`);
        console.error('Error deleting housing:', err);
      }
    }
  };

  const handleOpenAddModal = () => {
    setModalMode('add');
    setEditingHousing(undefined);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (housing: Housing) => {
    setModalMode('edit');
    setEditingHousing(housing);
    setIsModalOpen(true);
  };

  const handleSubmitHousing = () => {
    // Refresh the housing list after successful submission
    fetchHousings();
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Gestion des Logements</Title>
            <Subtitle>Gérez le catalogue de logements disponibles</Subtitle>
          </HeaderLeft>
          <AddButton onClick={handleOpenAddModal}>
            <FiPlus /> Ajouter un logement
          </AddButton>
        </Header>

        <StatsGrid>
          <StatCard>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total logements</StatLabel>
          </StatCard>

          <StatCard>
            <StatValue style={{ color: theme.colors.success }}>{stats.available}</StatValue>
            <StatLabel>Disponibles</StatLabel>
          </StatCard>

          <StatCard>
            <StatValue style={{ color: theme.colors.info }}>{stats.attributed}</StatValue>
            <StatLabel>Attribués</StatLabel>
          </StatCard>

          <StatCard>
            <StatValue style={{ color: theme.colors.warning }}>{stats.maintenance}</StatValue>
            <StatLabel>En maintenance</StatLabel>
          </StatCard>
        </StatsGrid>

        <FiltersBar>
          <SearchInput
            type="text"
            placeholder="Rechercher par référence, adresse, ville..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">Tous les types</option>
            <option value="T1">T1</option>
            <option value="T2">T2</option>
            <option value="T3">T3</option>
            <option value="T4">T4</option>
            <option value="T5">T5+</option>
          </FilterSelect>

          <FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="available">Disponible</option>
            <option value="attributed">Attribué</option>
            <option value="maintenance">Maintenance</option>
          </FilterSelect>
        </FiltersBar>

        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Référence</TableHeader>
                <TableHeader>Adresse</TableHeader>
                <TableHeader>Caractéristiques</TableHeader>
                <TableHeader>Loyer</TableHeader>
                <TableHeader>Disponibilité</TableHeader>
                <TableHeader>Statut</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </TableHead>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: theme.spacing.xl }}>
                    Chargement des logements...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.error }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : housings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: theme.spacing.xl }}>
                    Aucun logement trouvé
                  </TableCell>
                </TableRow>
              ) : (
                housings.map(housing => (
                  <TableRow key={housing.id}>
                    <TableCell>
                      <HousingReference>{housing.reference}</HousingReference>
                      <HousingType>{housing.type}</HousingType>
                    </TableCell>

                    <TableCell>
                      <AddressInfo>
                        <FiMapPin />
                        <AddressText>
                          {housing.address}<br />
                          {housing.postal_code} {housing.city}
                        </AddressText>
                      </AddressInfo>
                    </TableCell>

                    <TableCell>
                      <FeatureList>
                        <FeatureItem>
                          <FiSquare /> {housing.surface} m²
                        </FeatureItem>
                        <FeatureItem>
                          <FiHome /> {housing.rooms} pièces
                        </FeatureItem>
                        <FeatureItem>
                          📷 {housing.photos_count || housing.photos?.length || 0} photos
                        </FeatureItem>
                      </FeatureList>
                    </TableCell>

                    <TableCell>
                      <FeatureList>
                        <FeatureItem>
                          <FiDollarSign /> {housing.rent} FCFA/mois
                        </FeatureItem>
                        <div style={{ fontSize: theme.typography.sizes.xs, color: theme.colors.text + '60' }}>
                          + {housing.charges} FCFA charges
                        </div>
                      </FeatureList>
                    </TableCell>

                    <TableCell>
                      <FeatureItem>
                        <FiCalendar />
                        {housing.available_date ? new Date(housing.available_date).toLocaleDateString('fr-FR') : 'N/A'}
                      </FeatureItem>
                    </TableCell>

                    <TableCell>
                      <StatusBadge status={housing.status || 'available'}>
                        {getStatusIcon(housing.status || 'available')}
                        {getStatusLabel(housing.status || 'available')}
                      </StatusBadge>
                    </TableCell>

                    <TableCell>
                      <ActionButtons>
                        <ActionButton
                          variant="primary"
                          onClick={() => navigate(`/admin/housing/${housing.id}`)}
                          title="Voir détails"
                        >
                          <FiEye />
                        </ActionButton>
                        <ActionButton
                          onClick={() => handleOpenEditModal(housing)}
                          title="Modifier"
                        >
                          <FiEdit />
                        </ActionButton>
                        <ActionButton
                          variant="danger"
                          onClick={() => housing.id && handleDelete(housing.id)}
                          title="Supprimer"
                        >
                          <FiTrash2 />
                        </ActionButton>
                      </ActionButtons>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>

        {/* Modal d'ajout/modification de logement */}
        <HousingFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmitHousing}
          initialData={editingHousing}
          mode={modalMode}
        />
      </Container>
    </DashboardLayout>
  );
};
